import styled from "@emotion/styled";
import { useLocation } from "react-router-dom";
import { Link } from "atoms";
import chevronSrc from "atoms/icons/chevron.svg";
import { preventForwarding } from "styles/styleUtils";
import { paths } from "utils/paths";
import { useShopData } from "features/shop";

type TabLink = {
  label: string;
  to: (id: string) => string;
};
const tablinks: TabLink[] = [
  { label: "Featured", to: paths.artistProfile },
  { label: "Shop", to: paths.artistShop },
  { label: "Portfolio", to: paths.artistPortfolio },
  { label: "Updates", to: paths.artistUpdates },
  { label: "Reviews", to: paths.artistReviews },
  { label: "About", to: paths.artistAbout },
];

type ArtistProfileTabsProps = {
  artistId: string;
};

function ArtistProfileTabs({ artistId }: ArtistProfileTabsProps) {
  const { pathname } = useLocation();
  const { data } = useShopData();
  const hasShop = data?.artistsWithShop.includes(artistId);

  return (
    <TabsWrapperWrapper allowScroll={hasShop}>
      <TabsWrapper>
        <TabRow allowScroll={hasShop} aria-label="Artist Profile">
          {tablinks
            .filter(({ label }) => label !== "Shop" || hasShop)
            .map(({ label, to }) => (
              <ArtistTabLink
                key={label}
                to={to(artistId)}
                isActive={
                  label === "Featured"
                    ? pathname === to(artistId)
                    : pathname.startsWith(to(artistId))
                }
              >
                {label}
              </ArtistTabLink>
            ))}
        </TabRow>
      </TabsWrapper>
    </TabsWrapperWrapper>
  );
}

const bpScroll = "@media (min-width: 440px)";

const TabsWrapperWrapper = styled.div<{
  allowScroll?: boolean;
}>(({ allowScroll, theme }) => ({
  marginBottom: theme.spacing.lg,
  overflow: "hidden",
  position: "relative",
  height: 54,
  ...(allowScroll && {
    "&::before": {
      display: "block",
      bottom: 10,
      right: 0,
      content: "''",
      opacity: 1,
      position: "absolute",
      top: 0,
      width: 50,
      zIndex: 9,
      pointerEvents: "none",
      backgroundImage: `
      linear-gradient(to left, 
        rgba(255, 255, 255, 1.000) 0%,
        rgba(255, 255, 255, 0.838) 15%, 
        rgba(255, 255, 255, 0.501) 30%, 
        rgba(255, 255, 255, 0.382) 47%,
        rgba(255, 255, 255, 0.278) 56.5%, 
        rgba(255, 255, 255, 0.194) 65%, 
        rgba(255, 255, 255, 0.126) 73%,
        rgba(255, 255, 255, 0.075) 80.2%, 
        rgba(255, 255, 255, 0.042) 86.1%, 
        rgba(255, 255, 255, 0.021) 91%,
        rgba(255, 255, 255, 0.008) 95.2%, 
        rgba(255, 255, 255, 0.002) 98.2%,
        rgba(255, 255, 255, 0.000) 100%
        )`,
    },
    "&::after": {
      aspectRatio: 1,
      backgroundImage: `url('${chevronSrc}')`,
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: 11.5,
      borderRadius: theme.borderRadius.round,
      display: "block",
      right: 0,
      content: "''",
      opacity: 0.6,
      position: "absolute",
      top: "50%",
      transform: "rotate(90deg) translate(0, 3px)",
      backgroundColor: theme.colors.bg,
      transformOrigin: "center",
      width: 12,
      zIndex: 10,
    },
  }),
  [bpScroll]: {
    "&::before, &::after": {
      display: "none",
    },
  },
}));

const TabsWrapper = styled.div(({ theme }) => ({
  position: "relative",
  width: "100vw",
  scrollSnapType: "y mandatory",
  scrollPadding: theme.spacing.gap,
  overflow: "auto",
  paddingBottom: 6,
}));

const TabRow = styled.nav<{
  allowScroll?: boolean;
}>(({ allowScroll, theme }) => ({
  marginLeft: "auto",
  marginRight: "auto",
  maxWidth: 1440,
  minWidth: "max-content",
  width: "auto",
  scrollSnapAlign: "start",
  scrollSnapType: "x mandatory",
  scrollPadding: theme.spacing.gutter,
  scrollbarColor: "rgba(0,0,0,.2) rgba(0,0,0,0)",
  scrollbarWidth: "none",
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  flexWrap: "nowrap",
  padding: `${theme.spacing.sm} 0`,
  gap: "3vw",
  paddingLeft: 10,
  paddingRight: allowScroll ? 30 : 10,
  overflow: "hidden",
  [bpScroll]: {
    paddingInline: theme.spacing.gutter,
  },
  [theme.breakpoints.xs]: {
    paddingInline: theme.spacing.gutter,
  },
  [theme.breakpoints.tablet]: {
    gap: 10,
  },
  "&::after": {
    content: "''",
    display: "block",
    position: "absolute",
    bottom: `calc(${theme.spacing.sm} + 5px)`,
    height: 1,
    left: 0,
    right: 0,
    zIndex: 1,
    backgroundColor: theme.colors.fg20,
    boxShadow: `50vw 0 0 0 ${theme.colors.fg20}`,
  },
}));

const ArtistTabLink = styled(Link, {
  shouldForwardProp: preventForwarding("isActive"),
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  ...theme.fonts.display,
  fontSize: 14,
  color: isActive ? theme.colors.fg : theme.colors.fg70,
  textTransform: "uppercase",
  textDecoration: "none",
  lineHeight: 1,
  paddingTop: theme.spacing.md,
  paddingBottom: theme.spacing.sm,
  display: "inline-block",
  position: "relative",
  transform: "translateY(2px)",
  transition: ".3s ease color",
  zIndex: 2,
  "@media (min-width: 350px)": {
    fontSize: 15,
  },
  [theme.breakpoints.xs]: {
    fontSize: theme.fontSizes.sm,
  },
  [theme.breakpoints.tablet]: {
    paddingLeft: theme.spacing.xs,
    paddingRight: theme.spacing.xs,
  },
  "&:hover, &:focus, &:focus-visible": {
    color: theme.colors.fg,
  },
  "&::after": {
    content: "''",
    display: "block",
    position: "absolute",
    bottom: 0,
    height: 3,
    width: "100%",
    backgroundColor: theme.colors.fg,
    borderRadius: theme.borderRadius.round,
    opacity: isActive ? 1 : 0,
    transition: ".3s ease opacity",
    [theme.breakpoints.tablet]: {
      width: `calc(100% - (${theme.spacing.xs} * 2))`,
    },
  },
}));

export { ArtistProfileTabs };
